/**
 * Hashtags Parser
 *
 * Pure utilities that mirror the #hashtag handling in Flutter's
 * set_content_description_screen.dart (_handleHashTags, _selectHashtag,
 * _processContentHashTags).
 *
 * These are deliberately framework-agnostic so they can be reused by
 * other features.
 */

import type { ContentTagEntity } from '@/domain/entities/contentTag/contentTagEntity';

export interface HashtagAtCursor {
  /** The in-progress hashtag text, without the leading '#' (may be empty). */
  hashtag: string;
  /**
   * Position of the first character of the hashtag (the character AFTER '#').
   * Matches Flutter's `_hashTagStartPosition`.
   */
  startPos: number;
  /**
   * Position one past the last character of the hashtag (cursor/word end).
   * Matches Flutter's `_hashTagEndPosition`.
   */
  endPos: number;
}

/**
 * If the cursor is inside a word that contains a '#', return the current
 * hashtag token; otherwise null. The word boundary extends to the next space
 * after the cursor, or end-of-string.
 *
 * Mirrors Flutter's _handleHashTags word-boundary logic.
 */
export function parseHashtagAtCursor(text: string, cursorPos: number): HashtagAtCursor | null {
  if (cursorPos < 0) return null;

  let nextBlankSpacePosition = 0;
  if (cursorPos > 0) {
    nextBlankSpacePosition = text.indexOf(' ', cursorPos - 1);
    if (nextBlankSpacePosition === -1) {
      nextBlankSpacePosition = text.length;
    }
  }

  const wordLastCharPos = Math.max(cursorPos, nextBlankSpacePosition);
  const prefixText = text.substring(0, wordLastCharPos);
  const words = prefixText.split(' ');
  if (words.length === 0) return null;

  const wordContainingHashtag = words[words.length - 1];
  if (!wordContainingHashtag) return null;

  const hashIndex = wordContainingHashtag.lastIndexOf('#');
  if (hashIndex === -1) return null;

  const currentHashtag = wordContainingHashtag.substring(hashIndex + 1).toLowerCase();
  const endPos = wordLastCharPos;
  const startPos = endPos - currentHashtag.length;

  return { hashtag: currentHashtag, startPos, endPos };
}

/**
 * Replace the in-progress hashtag at [startPos, endPos) with `tagName ` (with
 * a trailing space). Mirrors Flutter's _selectHashtag.
 */
export function insertHashtag(
  text: string,
  startPos: number,
  endPos: number,
  tagName: string,
): { text: string; cursorPos: number } {
  const hashTagName = `${tagName.toLowerCase()} `;
  const tagLength = hashTagName.length;

  const prefixText = text.substring(0, startPos);
  const suffixText = text.substring(endPos);

  const newText = prefixText + hashTagName + suffixText;
  const cursorPos = startPos + tagLength;

  return { text: newText, cursorPos };
}

/**
 * Process a description string to extract all hashtags.
 * Port of Flutter's _processContentHashTags.
 *
 * Splits by spaces, finds words with '#', splits by '#', creates
 * ContentTagEntity objects with localUses: 1, reassembles description
 * with proper spacing.
 */
export function processContentHashTags(description: string): {
  processedDescription: string;
  hashTags: string[];
  hashTagsObjects: ContentTagEntity[];
} {
  const hashTags: string[] = [];
  const hashTagsObjects: ContentTagEntity[] = [];
  let processedDescription = '';

  if (!description) {
    return { processedDescription: '', hashTags, hashTagsObjects };
  }

  const words = description.split(' ');
  for (const word of words) {
    if (!word) continue;

    const decorationIndex = word.indexOf('#');
    if (decorationIndex < 0) {
      processedDescription += processedDescription === '' ? word : ` ${word}`;
      continue;
    }

    const prefix = word.substring(0, decorationIndex);
    if (prefix) {
      processedDescription += ` ${prefix}`;
    }

    const decorationWord = word.substring(decorationIndex + 1);
    const tags = decorationWord.split('#');
    for (const tag of tags) {
      if (tag) {
        processedDescription += ` #${tag}`;
        const newHashTag: ContentTagEntity = {
          name: tag.toLowerCase(),
          uses: 0,
          localUses: 1,
        };
        hashTagsObjects.push(newHashTag);
        hashTags.push(newHashTag.name);
      }
    }
  }

  // Remove leading blank space if any
  if (processedDescription.length > 0 && processedDescription[0] === ' ') {
    processedDescription = processedDescription.substring(1);
  }

  return { processedDescription, hashTags, hashTagsObjects };
}
